import React from "react";
import { useTranslation } from "react-i18next";
import "../../styles/ItemManagement.css";
import {
    Actions,
    Badge,
    Box,
    Button,
    Column,
    Columns,
    IconAdd,
    IconEdit,
    Inline,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    TextField,
    TooltipRenderer,
} from "braid-design-system";
import { ItemResponse } from "../../services/itemService";

interface ItemListTableProps {
    itemList?: Array<ItemResponse> | null;
    onEditItem?: (itemCode: string) => void;
    onCreateItem?: () => void;
    currentPage: number;
    hasNextPage: boolean;
    onPreviousPage: () => void;
    onNextPage: () => void;
    searchTerm: string;
    selectedStatus: string;
    onSearchChange: (value: string) => void;
    onStatusChange: (value: string) => void;
}

const ItemListTable: React.FC<ItemListTableProps> = ({
    itemList,
    onEditItem,
    onCreateItem,
    currentPage,
    hasNextPage,
    onPreviousPage,
    onNextPage,
    searchTerm,
    selectedStatus,
    onSearchChange,
    onStatusChange,
}) => {
    const { t } = useTranslation();

    return (
        <Stack space="large">
            <Columns space="large" collapseBelow="tablet">
                <Column width="1/3">
                    <Box display="flex" justifyContent="flexStart">
                        <Actions>
                            {onCreateItem ? (
                                <Button
                                    variant="ghost"
                                    tone="brandAccent"
                                    icon={<IconAdd />}
                                    onClick={onCreateItem}
                                >
                                    {t("page.item_management.list.button.create_new", { defaultValue: "Create New Item" })}
                                </Button>
                            ) : null}
                        </Actions>
                    </Box>
                </Column>
                <Column width="2/3">
                    <Box display="flex" justifyContent="flexEnd" alignItems="center" gap="medium">
                        <TextField
                            placeholder={t("page.item_management.list.search.placeholder", { defaultValue: "Search Items..." })}
                            aria-label={t("page.item_management.list.search.aria", { defaultValue: "Search Items" })}
                            onChange={(e) => onSearchChange(e.currentTarget.value)}
                            value={searchTerm}
                        />
                        <select
                            id="status-filter"
                            aria-label={t("page.item_management.list.status_filter.aria", { defaultValue: "Status Filter" })}
                            title={t("page.item_management.list.status_filter.aria", { defaultValue: "Status Filter" })}
                            value={selectedStatus}
                            onChange={(e) => onStatusChange(e.currentTarget.value)}
                            className="item-management-status-filter"
                        >
                            <option value="">{t("page.item_management.list.status_filter.all", { defaultValue: "All Status" })}</option>
                            <option value="1">{t("page.item_management.list.status_filter.active", { defaultValue: "Active" })}</option>
                            <option value="0">{t("page.item_management.list.status_filter.inactive", { defaultValue: "Inactive" })}</option>
                        </select>
                    </Box>
                </Column>
            </Columns>

            <Table label={t("page.item_management.list.table.label", { defaultValue: "Items List Table" })}>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell>
                            <Text>{t("page.item_management.list.table.headers.code", { defaultValue: "Code" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>{t("page.item_management.list.table.headers.name", { defaultValue: "Name" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>{t("page.item_management.list.table.headers.barcode", { defaultValue: "Barcode" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>{t("page.item_management.list.table.headers.qrcode", { defaultValue: "QR Code" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>{t("page.item_management.list.table.headers.vendorplu", { defaultValue: "Vendor PLU" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="right">
                            <Text>{t("page.item_management.list.table.headers.reordermin", { defaultValue: "Reorder Point" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="right">
                            <Text>{t("page.item_management.list.table.headers.reordermax", { defaultValue: "Reorder Quantity" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="right">
                            <Text>{t("page.item_management.list.table.headers.selling_price", { defaultValue: "Selling" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="right">
                            <Text>{t("page.item_management.list.table.headers.online_selling_price", { defaultValue: "Online Selling" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_management.list.table.headers.reorder_alert", { defaultValue: "Reorder" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_management.list.table.headers.pos_item", { defaultValue: "POS" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_management.list.table.headers.dir_grn", { defaultValue: "GRN" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_management.list.table.headers.ho_price", { defaultValue: "HO" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_management.list.table.headers.generate_batch", { defaultValue: "Batch" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_management.list.table.headers.allow_st_code", { defaultValue: "ST" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_management.list.table.headers.item_classification", { defaultValue: "Item Type" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_management.list.table.headers.active_discount", { defaultValue: "Disc" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_management.list.table.headers.weighted", { defaultValue: "Wt" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_management.list.table.headers.is_returnable", { defaultValue: "Returnable" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center">
                            <Text>{t("page.item_management.list.table.headers.status", { defaultValue: "Status" })}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="right">
                            <Text>{t("page.item_management.list.table.headers.actions", { defaultValue: "Actions" })}</Text>
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {itemList && itemList.length > 0 ? (
                        itemList.map((item) => (
                            <TableRow key={item.item_code ?? `${item.item_id}`}>
                                <TableCell>
                                    <Text>{item.item_code || String(t("page.item_management.list.table.na", { defaultValue: "N/A" }))}</Text>
                                </TableCell>
                                <TableCell>
                                    <Text>{item.item_name || String(t("page.item_management.list.table.na", { defaultValue: "N/A" }))}</Text>
                                </TableCell>
                                <TableCell>
                                    <Text>{item.item_barcode || String(t("page.item_management.list.table.na", { defaultValue: "N/A" }))}</Text>
                                </TableCell>
                                <TableCell>
                                    <Text>{item.item_qrcode || String(t("page.item_management.list.table.na", { defaultValue: "N/A" }))}</Text>
                                </TableCell>
                                <TableCell>
                                    <Text>{item.item_vendorplu || String(t("page.item_management.list.table.na", { defaultValue: "N/A" }))}</Text>
                                </TableCell>
                                <TableCell align="right">
                                    <Text>{item.item_reordermin != null ? item.item_reordermin : String(t("page.item_management.list.table.na", { defaultValue: "N/A" }))}</Text>
                                </TableCell>
                                <TableCell align="right">
                                    <Text>{item.item_reordermax != null ? item.item_reordermax : String(t("page.item_management.list.table.na", { defaultValue: "N/A" }))}</Text>
                                </TableCell>
                                <TableCell align="right">
                                    <Text>{item.item_selling != null ? item.item_selling : String(t("page.item_management.list.table.na", { defaultValue: "N/A" }))}</Text>
                                </TableCell>
                                <TableCell align="right">
                                    <Text>{item.item_onlselling != null ? item.item_onlselling : String(t("page.item_management.list.table.na", { defaultValue: "N/A" }))}</Text>
                                </TableCell>
                                <TableCell align="center">
                                    {item.item_enablereorderalert ? <Badge tone="positive">✓</Badge> : <Badge tone="neutral">-</Badge>}
                                </TableCell>
                                <TableCell align="center">
                                    {item.item_positem ? <Badge tone="positive">✓</Badge> : <Badge tone="neutral">-</Badge>}
                                </TableCell>
                                <TableCell align="center">
                                    {item.item_dirgrn ? <Badge tone="positive">✓</Badge> : <Badge tone="neutral">-</Badge>}
                                </TableCell>
                                <TableCell align="center">
                                    {item.item_hoprice ? <Badge tone="positive">✓</Badge> : <Badge tone="neutral">-</Badge>}
                                </TableCell>
                                <TableCell align="center">
                                    {item.item_generagebatch ? <Badge tone="positive">✓</Badge> : <Badge tone="neutral">-</Badge>}
                                </TableCell>
                                <TableCell align="center">
                                    {item.item_allowstcode ? <Badge tone="positive">✓</Badge> : <Badge tone="neutral">-</Badge>}
                                </TableCell>
                                <TableCell align="center">
                                    {item.item_calreceipe ? (
                                        <Badge tone="positive">{t("page.item_management.list.badge.finished_product", { defaultValue: "Finished Product" })}</Badge>
                                    ) : item.item_rawitem ? (
                                        <Badge tone="info">{t("page.item_management.list.badge.raw_material", { defaultValue: "Raw Material" })}</Badge>
                                    ) : (
                                        <Badge tone="neutral">-</Badge>
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {item.item_activedis ? <Badge tone="positive">✓</Badge> : <Badge tone="neutral">-</Badge>}
                                </TableCell>
                                <TableCell align="center">
                                    {item.item_weighted ? <Badge tone="positive">✓</Badge> : <Badge tone="neutral">-</Badge>}
                                </TableCell>
                                <TableCell align="center">
                                    {item.is_returnable ? <Badge tone="positive">✓</Badge> : <Badge tone="neutral">-</Badge>}
                                </TableCell>
                                <TableCell align="center">
                                    {item.item_active ? (
                                        <Badge tone="positive">{t("page.item_management.list.badge.active", { defaultValue: "Active" })}</Badge>
                                    ) : (
                                        <Badge tone="caution">{t("page.item_management.list.badge.inactive", { defaultValue: "Inactive" })}</Badge>
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <Inline space="small">
                                        <TooltipRenderer tooltip={<Text>{t("page.item_management.list.tooltip.edit", { defaultValue: "Edit Item" })}</Text>}>
                                            {({ triggerProps }) => (
                                                <Box
                                                    onClick={() => {
                                                        if (item.item_code) {
                                                            onEditItem?.(item.item_code);
                                                        }
                                                    }}
                                                    cursor={item.item_code ? "pointer" : "default"}
                                                    {...triggerProps}
                                                >
                                                    <IconEdit tone="info" />
                                                </Box>
                                            )}
                                        </TooltipRenderer>
                                    </Inline>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colspan={21} align="center">
                                <Text>{t("page.item_management.list.empty", { defaultValue: "No items found." })}</Text>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <Columns space="small" collapseBelow="tablet">
                <Column width="1/3">
                    <Text>{t("page.item_management.list.pagination.page", { defaultValue: `Page ${currentPage}` })}</Text>
                </Column>
                <Column width="2/3">
                    <Box display="flex" justifyContent="flexEnd">
                        <Inline space="small">
                            <Button
                                variant="ghost"
                                tone="neutral"
                                onClick={currentPage > 1 ? onPreviousPage : undefined}
                            >
                                {t("page.item_management.list.pagination.previous", { defaultValue: "Previous" })}
                            </Button>
                            <Button
                                variant="ghost"
                                tone="brandAccent"
                                onClick={hasNextPage ? onNextPage : undefined}
                            >
                                {t("page.item_management.list.pagination.next", { defaultValue: "Next" })}
                            </Button>
                        </Inline>
                    </Box>
                </Column>
            </Columns>
        </Stack>
    );
};

export default ItemListTable;
